"use strict";
// The names of the files the app saves to a job's SharePoint site (proposals,
// Yearly Summaries, take-offs): SharePoint caps a name, and the API cuts a longer
// one from its end (graph.ts safeName), which lost the date and let a later
// proposal replace an earlier one. Only the free-text parts are shortened here.
Object.defineProperty(exports, "__esModule", { value: true });
exports.FILE_NAME_MAX = void 0;
exports.fitFileName = fitFileName;
/** The longest file name the app saves (the API's safeName cuts anything longer). */
exports.FILE_NAME_MAX = 100;
/** A short ending fixed by the text (FNV-1a, base 36), so two long names shortened to the same start stay apart. */
function nameTag(text) {
    let hash = 0x811c9dc5;
    for (const ch of text)
        hash = Math.imul(hash ^ (ch.codePointAt(0) ?? 0), 0x01000193) >>> 0;
    return hash.toString(36);
}
const textOf = (piece) => (typeof piece === 'string' ? piece : piece.shortenable);
/**
 * The pieces joined, fitted to `maxLength` by shortening the free-text pieces
 * (each from its end, the first one first), never the fixed ones: the option,
 * the date and the extension always survive. With `tagged`, the last piece
 * shortened ends with a tag from the free text (" -1x9k2a"; SharePoint refuses "~"), for names whose
 * free text is what tells two files apart. A name that fits is left as it is, unless `alwaysTagged`: then the last
 * free piece always ends with the tag, for free text that cleaning or SharePoint's case-blind names could make clash.
 */
function fitFileName(pieces, maxLength = exports.FILE_NAME_MAX, { tagged = false, alwaysTagged = false } = {}) {
    const whole = pieces.map(textOf).join('');
    if (whole.length <= maxLength && !alwaysTagged)
        return whole;
    const free = pieces.filter((p) => typeof p !== 'string').map(textOf);
    const tag = tagged || alwaysTagged ? ` -${nameTag(free.join(''))}` : '';
    let over = whole.length + tag.length - maxLength;
    const out = pieces.map((piece) => {
        if (typeof piece === 'string' || over <= 0)
            return { text: textOf(piece), cut: false };
        const keep = Math.max(0, piece.shortenable.length - over);
        over -= piece.shortenable.length - keep;
        return { text: piece.shortenable.slice(0, keep).trim(), cut: true };
    });
    const lastCut = out.map((p) => p.cut).lastIndexOf(true);
    // Nothing needed cutting (an always-tagged name that fits): the tag goes on the last free piece.
    const tagAt = lastCut >= 0 ? lastCut : pieces.map((p) => typeof p !== 'string').lastIndexOf(true);
    return out.map((p, i) => (i === tagAt ? p.text + tag : p.text)).join('');
}
